import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, SETTING_TYPE, useGetGlobalSettingsQuery } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { useTheme } from '../context/ThemeContext';
import type { ThemeMode } from '../hooks/useDarkMode';

const THEME_OPTIONS: ReadonlyArray<{ mode: ThemeMode; icon: string; labelKey: string }> = [
  { mode: 'dark', icon: 'solar:moon-bold', labelKey: 'Dark' },
  { mode: 'light', icon: 'solar:sun-bold', labelKey: 'Light' },
];

/**
 * Dark / Light segmented control shown in the app header when the tenant
 * "Dark Mode" capability is active. When inactive (or missing), the control
 * is hidden and appearance is forced to light without clearing the user's
 * stored theme preference.
 */
export function ThemeModeSwitcher() {
  const { t } = useTranslation();
  const { mode, setMode, isDarkModeFeatureEnabled, setDarkModeFeatureEnabled } = useTheme();
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: settings, isSuccess, isError } = useGetGlobalSettingsQuery(undefined, {
    skip: !token,
  });

  const isDarkModeEnabled = useMemo(
    () => Boolean(settings?.find((item) => item.ability === SETTING_TYPE.DARK_MODE && item.is_active)),
    [settings]
  );

  const settingsResolved = isSuccess || isError;

  // Apply the tenant capability gate without calling setMode('light'), so the
  // user's preferred mode stays in localStorage and is restored when re-enabled.
  useEffect(() => {
    if (!token || !settingsResolved) return;
    setDarkModeFeatureEnabled(isDarkModeEnabled);
  }, [token, settingsResolved, isDarkModeEnabled, setDarkModeFeatureEnabled]);

  if (token && (!settingsResolved || !isDarkModeFeatureEnabled)) return null;

  return (
    <div
      className="flex items-center gap-0.5 p-0.5 sm:p-1 rounded-full border"
      style={{ borderColor: 'var(--cty-border)', backgroundColor: 'var(--cty-bg-elevated)' }}
      role="radiogroup"
      aria-label={t('Theme')}
    >
      {THEME_OPTIONS.map(({ mode: optionMode, icon, labelKey }) => {
        const isActive = mode === optionMode;
        return (
          <CtyButton
            key={optionMode}
            isIconOnly
            size="sm"
            radius="full"
            variant={isActive ? 'solid' : 'light'}
            color={isActive ? 'primary' : 'default'}
            className="min-w-7 w-7 h-7 sm:min-w-8 sm:w-8 sm:h-9"
            title={t(labelKey)}
            tooltipContent={t(labelKey)}
            onPress={() => setMode(optionMode)}
          >
            <Icon icon={icon} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </CtyButton>
        );
      })}
    </div>
  );
}

export default ThemeModeSwitcher;
